import React, { createContext, useContext, useRef, useState, useEffect } from "react";

const AudioCtx = createContext(null);

export const useAudioPlayer = () => useContext(AudioCtx);

export const AudioPlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null); // episode object
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  // Load & autoplay when the current track changes
  useEffect(() => {
    const a = audioRef.current;
    if (current && a) {
      a.src = current.audioUrl;
      a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      setProgress(0);
    }
  }, [current]);

  const play = (episode) => {
    if (!episode) return;
    if (current && current.id === episode.id) {
      togglePlay();
      return;
    }
    setCurrent(episode);
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) {
      a.play();
      setIsPlaying(true);
    } else {
      a.pause();
      setIsPlaying(false);
    }
  };

  const seek = (val) => {
    const a = audioRef.current;
    if (a && !isNaN(val)) {
      a.currentTime = val;
      setProgress(val);
    }
  };

  const close = () => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.removeAttribute("src");
      a.load();
    }
    setCurrent(null);
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
  };

  const isCurrent = (id) => current && current.id === id;

  // Add breathing room at the bottom so the fixed bar never hides content.
  useEffect(() => {
    document.body.classList.toggle("has-audio-bar", !!current);
    return () => document.body.classList.remove("has-audio-bar");
  }, [current]);

  return (
    <AudioCtx.Provider
      value={{ current, isPlaying, progress, duration, play, togglePlay, seek, close, isCurrent, hasTrack: !!current }}
    >
      {children}
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => setProgress(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => setIsPlaying(false)}
        preload="metadata"
      />
    </AudioCtx.Provider>
  );
};
